import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export type IsoMountOperationStatus = 'in_progress' | 'completed' | 'failed'

export interface IsoMountOperationState {
  id: string
  vmId: string
  vmName?: string
  isoImageId: string
  isoImageName?: string
  status: IsoMountOperationStatus
  error?: string | null
  failedVolumeId?: string | null
  createdAt: number
}

interface IsoMountProgressContextValue {
  operations: Record<string, IsoMountOperationState>
  addOperation: (operation: IsoMountOperationState) => void
  updateOperation: (id: string, payload: Partial<IsoMountOperationState>) => void
  removeOperation: (id: string) => void
  clearOperations: () => void
}

const IsoMountProgressContext = createContext<IsoMountProgressContextValue | null>(null)

export const useIsoMountProgress = () => {
  const context = useContext(IsoMountProgressContext)
  if (!context) {
    throw new Error('useIsoMountProgress must be used within an IsoMountProgressProvider')
  }
  return context
}

export const IsoMountProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [operations, setOperations] = useState<Record<string, IsoMountOperationState>>({})

  const addOperation = useCallback((operation: IsoMountOperationState) => {
    setOperations((prev) => ({
      ...prev,
      [operation.id]: operation,
    }))
  }, [])

  const updateOperation = useCallback((id: string, payload: Partial<IsoMountOperationState>) => {
    setOperations((prev) => {
      if (!prev[id]) {
        return prev
      }
      return {
        ...prev,
        [id]: {
          ...prev[id],
          ...payload,
        },
      }
    })
  }, [])

  const removeOperation = useCallback((id: string) => {
    setOperations((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [])

  const clearOperations = useCallback(() => {
    setOperations({})
  }, [])

  const value = useMemo(
    () => ({
      operations,
      addOperation,
      updateOperation,
      removeOperation,
      clearOperations,
    }),
    [operations, addOperation, updateOperation, removeOperation, clearOperations],
  )

  return (
    <IsoMountProgressContext.Provider value={value}>{children}</IsoMountProgressContext.Provider>
  )
}
